import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { push } from 'react-router-redux';
import {
  Badge,
  Box,
  Icons,
  TextField,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import NavigationBackHeader from '@app/components/presentational/common/NavigationBackHeader/NavigationBackHeader';
import InfiniteScroller from '../../../../containers/common/InfiniteScroller';
import OutlookEmailRow from './components/OutlookEmailRow';
import {
  OutlookAllEmailsSkeleton,
  OutlookAllEmailsRowSkeleton,
} from './components/OutlookAllEmailsSkeleton';
import useOutlookEmails from './useOutlookEmails';
import { OutlookMessage } from './types';
import { NAVIGATION_SOURCE_OUTLOOK_VIEW_ALL_MODAL } from '@core/constants/analytics';
import {
  trackOutlookEmailOpened,
  trackOutlookSearched,
} from './outlookAnalytics';

const OutlookAllEmailsMobileScreen: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const searchTextSecondary = theme.palette.text.secondary;
  const {
    emails,
    hasMoreEmails,
    isLoadingEmails,
    isLoadingMoreEmails,
    fetchNextPage,
    searchEmails,
    unreadCount,
  } = useOutlookEmails(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<OutlookMessage[] | null>(
    null
  );
  const [searchNextLink, setSearchNextLink] = useState<string | null>(null);
  const [isLoadingMoreSearch, setIsLoadingMoreSearch] = useState(false);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setSearchResults(null);
    setSearchNextLink(null);
    setIsSearching(false);
    setIsLoadingMoreSearch(false);
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (!value.trim()) {
      setSearchResults(null);
      setSearchNextLink(null);
      setIsSearching(false);
      setIsLoadingMoreSearch(false);
    }
  };

  const handleSearch = async () => {
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      handleClearSearch();
      return;
    }
    trackOutlookSearched();
    setIsSearching(true);
    setSearchResults(null);
    try {
      const result = await searchEmails(trimmed);
      setSearchResults(result.emails);
      setSearchNextLink(result.nextLink ?? null);
    } catch {
      setSearchResults([]);
      setSearchNextLink(null);
    } finally {
      setIsSearching(false);
    }
  };

  // Load next page of search results
  const loadMoreSearch = useCallback(async () => {
    if (!searchNextLink || isLoadingMoreSearch) return;
    setIsLoadingMoreSearch(true);
    try {
      const result = await searchEmails(undefined, searchNextLink);
      setSearchResults((prev) => [...(prev ?? []), ...result.emails]);
      setSearchNextLink(result.nextLink ?? null);
    } catch {
      // Keep existing results
    } finally {
      setIsLoadingMoreSearch(false);
    }
  }, [searchNextLink, isLoadingMoreSearch, searchEmails]);

  const isSearchMode = searchResults !== null || isSearching;
  const activeItems = isSearchMode ? searchResults ?? [] : emails;
  const activeHasMore = isSearchMode ? Boolean(searchNextLink) : hasMoreEmails;
  const activeLoading = isSearchMode
    ? isLoadingMoreSearch
    : isLoadingMoreEmails;
  const handleLoadMore = isSearchMode ? loadMoreSearch : fetchNextPage;

  const handleMessageClick = (message: OutlookMessage) => {
    const itemIndex = activeItems.findIndex((item) => item.id === message.id);
    trackOutlookEmailOpened(
      NAVIGATION_SOURCE_OUTLOOK_VIEW_ALL_MODAL,
      itemIndex >= 0 ? itemIndex : undefined
    );
    dispatch(
      push({ pathname: `/home/email/${message.id}`, state: { tracked: true } })
    );
  };

  const handleBack = () => {
    window.history.back();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        overflowY: 'hidden',
        p: 2,
        pt: 1,
        gap: 1.5,
        flexDirection: 'column',
      }}
    >
      <NavigationBackHeader onClick={handleBack} />
      <Box
        gap={1.5}
        bgcolor={theme.palette.background.paper}
        borderRadius={2}
        display="flex"
        flexDirection="column"
        sx={{
          flex: 1,
          p: 2,
        }}
      >
        <Box display="flex" alignItems="center">
          <Typography
            variant="subtitle1"
            component="h1"
            color="primary"
            sx={{
              fontSize: { xs: '15px', sm: '16px' },
              lineHeight: '24px',
              fontWeight: 500,
              fontFamily: 'Roboto, sans-serif',
            }}
          >
            Inbox
          </Typography>

          {unreadCount > 0 ? (
            <Badge
              badgeContent={unreadCount}
              sx={{
                ml: 2.5,
                '& .MuiBadge-badge': {
                  backgroundColor: '#e8dcfcff',
                  color: theme.palette.getContrastText('#e8dcfcff'),
                },
              }}
            />
          ) : null}
        </Box>

        {/* Search bar */}
        <TextField
          label="Search emails"
          placeholder="Search"
          value={searchTerm}
          onChange={handleSearchChange}
          leftElement={
            isSearching ? (
              <CircularProgress
                style={{ color: searchTextSecondary }}
                size={20}
              />
            ) : (
              <Icons.Search
                sx={{ color: searchTextSecondary }}
                fontSize="small"
              />
            )
          }
          rightElement={
            searchTerm !== '' ? (
              <Icons.Close
                sx={{ color: searchTextSecondary, cursor: 'pointer' }}
                fontSize="medium"
                onClick={handleClearSearch}
              />
            ) : undefined
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSearch();
            }
          }}
        />

        {/* Initial loading or search loading skeleton */}
        {((isSearching && searchResults === null) ||
          (isLoadingEmails && !isSearchMode && activeItems.length === 0)) && (
          <OutlookAllEmailsSkeleton count={6} />
        )}

        {/* No results */}
        {!isSearching && isSearchMode && searchResults?.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            py={3}
          >
            No emails found for &ldquo;{searchTerm.trim()}&rdquo;
          </Typography>
        )}

        {/* Virtualized Infinite Scroller */}
        {(!isSearching || searchResults !== null) &&
          (!isLoadingEmails || isSearchMode || activeItems.length > 0) &&
          (!isSearchMode || (searchResults && searchResults.length > 0)) && (
            <Box sx={{ flex: 1, minHeight: 0, height: '100%' }}>
              <InfiniteScroller
                items={activeItems}
                loading={activeLoading}
                hasMore={activeHasMore}
                loadMore={handleLoadMore}
                height="100%"
                ariaLabel="All emails list"
                footer={
                  activeLoading ? (
                    <Box py={1}>
                      <OutlookAllEmailsRowSkeleton compact />
                    </Box>
                  ) : null
                }
                renderItem={(message, index) => (
                  <Box key={message.id}>
                    <OutlookEmailRow
                      message={message}
                      isSelected={false}
                      compact
                      onClick={handleMessageClick}
                    />
                    {index < activeItems.length - 1 && (
                      <Divider sx={{ bgcolor: theme.palette.divider, my: 1 }} />
                    )}
                  </Box>
                )}
              />
            </Box>
          )}
      </Box>
    </Box>
  );
};

export default OutlookAllEmailsMobileScreen;
